// src/pages/personal/analytics/CustomerSection/components/TopClients.tsx
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/Layout';
import { DataList, DataListColumnDef } from '@/components/ui/DataList';
import { COLORS } from '@/theme';
// Импортируем форматтеры
import { formatShortName, formatNumber } from '@/utils/Formatters';
import { useBusinessCurrency } from '@/hooks/useBusinessCurrency';
// Импортируем иконку
import { User, Package, CreditCard } from 'lucide-react';

import styles from '../CustomerSection.module.css';

interface TopClient {
  id: string;
  name: string;
  phone: string;
  status: string;
  status_name: string;
  total_spent: number;
  orders_count: number;
  period_total: number;
  period_orders_count: number;
}

interface TopClientsProps {
  data: TopClient[];
  isLoading?: boolean;
}

const TopClients: React.FC<TopClientsProps> = ({ data, isLoading = false }) => {
  const { t, i18n } = useTranslation('customer');
  const { formatPrice } = useBusinessCurrency();
  const currentLocale = i18n.language;
  
  // Получаем цвета для статусов из темы
  const getStatusColor = (status: string): string => {
    const statusKey = status.toLowerCase() as keyof typeof COLORS.clientChartColors;
    return COLORS.clientChartColors[statusKey] || COLORS.clientChartColors.default;
  };
  
  // Определение колонок для списка
  const columns = useMemo<DataListColumnDef<TopClient>[]>(() => [
    {
      key: 'name',
      title: t('top_clients.name'),
      width: 'lg',
      render: (item: TopClient) => formatShortName(item.name),
      mobilePriority: 0 // Самый высокий приоритет
    },
    {
      key: 'status',
      title: t('top_clients.status'),
      width: 'md',
      render: (item: TopClient) => (
        <div className="flex items-center">
          <div 
            className="w-3 h-3 rounded-sm mr-2" 
            style={{ backgroundColor: getStatusColor(item.status) }}
          ></div>
          {t(`statuses.${item.status}`, { defaultValue: item.status_name })}
        </div>
      ),
      mobilePriority: 1
    },
    {
      key: 'orders_count',
      title: t('top_clients.orders'),
      width: 'sm',
      textAlign: 'right',
      render: (item: TopClient) => formatNumber(item.orders_count, 0, true, currentLocale),
      mobilePriority: 2
    },
    {
      key: 'total_spent',
      title: t('top_clients.total_spent'),
      width: 'md',
      textAlign: 'right',
      render: (item: TopClient) => formatPrice(item.total_spent),
      mobilePriority: 3
    },
    {
      key: 'period_total',
      title: t('top_clients.period_spent'),
      width: 'md',
      textAlign: 'right',
      render: (item: TopClient) => formatPrice(item.period_total),
      mobilePriority: 4
    }
  ], [t, formatPrice, currentLocale]);

  // Компонент для отображения загрузки
  const renderLoader = () => (
    <div className={styles.loadingContainer}>
      <div className={styles.loadingSpinner}></div>
      <p className={styles.loadingText}>{t('loading')}</p>
    </div>
  );
  
  // Компонент для отображения отсутствия данных
  const renderNoData = () => (
    <div className={styles.noDataContainer}>
      <p>{t('no_data')}</p>
    </div>
  );
  
  // Рендер компонента мобильной карточки для каждого клиента
  const renderMobileCard = (client: TopClient) => {
    return (
      <div key={client.id} className="mb-4 border rounded-lg overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow">
        <div 
          className="w-full h-1" 
          style={{ backgroundColor: getStatusColor(client.status) }}
        ></div>
        
        <div className="p-4">
          {/* Заголовок карточки с именем и статусом */}
          <div className="flex justify-between items-start mb-3">
            <div className="font-medium text-base">
              {formatShortName(client.name)}
            </div>
            <div className="flex items-center text-sm">
              <div 
                className="w-2 h-2 rounded-full mr-1" 
                style={{ backgroundColor: getStatusColor(client.status) }}
              ></div>
              {t(`statuses.${client.status}`, { defaultValue: client.status_name })}
            </div>
          </div>
          
          {/* Контент карточки */}
          <div className="grid grid-cols-2 gap-2">
            {/* Количество заказов */}
            <div className="flex items-center">
              <Package className="w-4 h-4 mr-2 text-gray-500" />
              <div>
                <div className="text-xs text-gray-500">{t('top_clients.orders')}</div>
                <div className="font-medium">{formatNumber(client.orders_count, 0, true, currentLocale)}</div>
              </div>
            </div>
            
            {/* Всего потрачено */}
            <div className="flex items-center">
              <CreditCard className="w-4 h-4 mr-2 text-gray-500" />
              <div>
                <div className="text-xs text-gray-500">{t('top_clients.total_spent')}</div>
                <div className="font-medium">{formatPrice(client.total_spent)}</div>
              </div>
            </div>
            
            {/* Потрачено за период */}
            <div className="flex items-center col-span-2 mt-1">
              <div className="w-4 h-4 mr-2"></div> {/* Spacer для выравнивания */}
              <div>
                <div className="text-xs text-gray-500">{t('top_clients.period_spent')}</div>
                <div className="font-medium">{formatPrice(client.period_total)}</div>
              </div>
            </div>
          </div>
          
          {/* Телефон, если есть */}
          {client.phone && (
            <div className="mt-3 text-sm text-gray-500 flex items-center">
              <User className="w-4 h-4 mr-2" />
              {client.phone}
            </div>
          )}
        </div>
      </div>
    );
  };
  
  // Компонент для мобильного отображения списка клиентов
  const renderMobileList = () => {
    return (
      <div className="p-4 md:hidden">
        {data.map(renderMobileCard)}
      </div>
    );
  };
  
  // Рендер контента в зависимости от состояния
  const renderContent = () => {
    if (isLoading) {
      return renderLoader();
    }
    
    if (!data || data.length === 0) {
      return renderNoData();
    }
    
    return (
      <>
        {/* Мобильное отображение в виде карточек */}
        {renderMobileList()}
        
        {/* Десктопное отображение в виде таблицы */}
        <div className="hidden md:block">
          <DataList 
            data={data}
            columns={columns}
            noDataText={t('no_data')}
            getItemColor={(item) => getStatusColor(item.status)}
            colorKey="status"
            className="w-full"
          />
        </div>
      </>
    );
  };

  return (
    <Card padding="none" shadow="sm" className="w-full">
      <Card.Header className={styles.cardHeader}>
        <h3 className={styles.cardTitle}>{t('top_clients.title')}</h3>
      </Card.Header>
      <Card.Body className={`${styles.cardContent} overflow-hidden`}>
        {renderContent()}
      </Card.Body>
    </Card>
  );
};

export default React.memo(TopClients);